import { useMemo, useState } from "react";

import { Box } from "@mui/material";
import DatasetCardsGrid from "./DatasetCardsGrid";
import DatasetFilters from "./DatasetFilters";
import SearchBar from "./SearchBar";
import { useDebounce } from "src/helpers/helperFunctions";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { whereFilterType } from "src/types/types";

export type QueryOptions = {
  searchQuery: string;
  filters?: whereFilterType[];
  folderId?: number;
};

type DatasetCardSearch = {
  atomResults?: boolean;
};

const DatasetCardSearch = ({ atomResults = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [userFilters, setUserFilters] = useState<whereFilterType[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const user = useGetUserDetails();
  const userName = user?.user_display_name;

  const whereFilters = useMemo(() => {
    const allExceptAtomResults: whereFilterType = {
      filter_on: {
        group: "dataset",
        name: "dataset_type.name",
        column: "dataset_type.name",
      },
      comparison_operator: "ne",
      value_datatype: "string",
      filter_value: "AtomResults",
    };

    const onlyAtomResults: whereFilterType[] = [
      {
        filter_on: {
          group: "dataset",
          name: "dataset_type.name",
          column: "dataset_type.name",
        },
        comparison_operator: "eq",
        value_datatype: "string",
        filter_value: "AtomResults",
      },
      {
        filter_on: {
          group: "user",
          name: "create_user.username",
          column: "create_user.username",
        },
        comparison_operator: "eq",
        value_datatype: "string",
        filter_value: userName ?? "",
      },
    ];

    return atomResults
      ? [...onlyAtomResults, ...userFilters]
      : [allExceptAtomResults, ...userFilters];
  }, [userFilters, atomResults, userName]);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const queryOptions = useMemo(() => {
    const options: QueryOptions = {
      searchQuery,
    };

    if (whereFilters) {
      options.filters = whereFilters;
    }

    return options;
  }, [searchQuery, whereFilters]);

  return (
    <Box>
      <SearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        placeholder="Search for datasets"
        data-testid="ds-search-input"
      />
      <Box
        display="flex"
        justifyContent="space-between"
        mb={1}
        flexWrap="wrap"
        gap={1}
      >
        <DatasetFilters
          whereFilters={userFilters}
          setWhereFilters={setUserFilters}
          atomResults={atomResults}
          showFavoritesOnly={showFavoritesOnly}
          onToggleFavorites={setShowFavoritesOnly}
        />
        <Box id="toggle-dataset-view" />
      </Box>
      <DatasetCardsGrid
        queryOptions={queryOptions}
        debouncedSearchQuery={debouncedSearchQuery}
        whereFilters={userFilters}
        atomResults={atomResults}
        showFavoritesOnly={showFavoritesOnly}
      />
    </Box>
  );
};

export default DatasetCardSearch;
